import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Mic, Square, Loader2, Sparkles, Save, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { createDream, transcribeAudio, analyzeDream } from '../lib/api';

const MOODS = [
  { value: 'peaceful', label: 'Peaceful' },
  { value: 'anxious', label: 'Anxious' },
  { value: 'confused', label: 'Confused' },
  { value: 'frightened', label: 'Frightened' },
  { value: 'excited', label: 'Excited' },
  { value: 'sad', label: 'Sad' },
  { value: 'relief', label: 'Relief' },
  { value: 'neutral', label: 'Neutral' },
];

export default function DreamCapture() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('voice'); // 'voice' or 'text'
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mood: '',
  });
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await handleTranscription(audioBlob);
        }
      };
      
      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };
  
  const handleTranscription = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const response = await transcribeAudio(audioBlob);
      const transcribedText = response.data.text;
      
      setFormData(prev => ({
        ...prev,
        content: prev.content ? `${prev.content}\n\n${transcribedText}` : transcribedText,
      }));
      
      toast.success('Dream transcribed successfully!');
    } catch (error) {
      console.error('Transcription failed:', error);
      toast.error('Failed to transcribe audio. Please try again or type manually.');
    } finally {
      setIsTranscribing(false);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleSave = async (shouldAnalyze = false) => {
    if (!formData.content.trim()) {
      toast.error('Please record or type your dream first');
      return;
    }
    
    const title = formData.title.trim() || generateTitle(formData.content);
    
    setIsSaving(true);
    try {
      const response = await createDream({
        title,
        content: formData.content,
        mood: formData.mood || null,
        tags: [],
      });
      
      const dreamId = response.data.dream_id;
      
      if (shouldAnalyze) {
        toast.loading('Analyzing your dream...', { id: 'analysis' });
        try {
          await analyzeDream(dreamId);
          toast.success('Dream saved and analyzed!', { id: 'analysis' });
        } catch (error) {
          toast.error('Analysis failed, but dream was saved', { id: 'analysis' });
        }
      } else {
        toast.success('Dream saved successfully!');
      }
      
      navigate(`/dream/${dreamId}`);
    } catch (error) {
      console.error('Failed to save dream:', error);
      toast.error('Failed to save dream');
    } finally {
      setIsSaving(false);
    }
  };
  
  const generateTitle = (content) => {
    const words = content.split(' ').slice(0, 5).join(' ');
    return words.length > 30 ? words.substring(0, 30) + '...' : words;
  };
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="dream-capture-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 pt-8 text-center">
          <h1 className="text-2xl font-serif font-bold mb-1">Capture Your Dream</h1>
          <p className="text-muted-foreground text-sm">
            {mode === 'voice' 
              ? 'Tap and speak your dream at your own pace'
              : 'Type your dream in your own words'
            }
          </p>
        </header>
        
        {/* Mode Toggle */}
        <div className="px-6 mb-6">
          <div className="flex gap-2 glass-card p-1">
            <Button
              variant={mode === 'voice' ? 'default' : 'ghost'}
              className={`flex-1 rounded-xl ${mode === 'voice' ? '' : 'hover:bg-white/5'}`}
              onClick={() => setMode('voice')}
              data-testid="voice-mode-btn"
            >
              <Mic className="w-4 h-4 mr-2" />
              Voice
            </Button>
            <Button
              variant={mode === 'text' ? 'default' : 'ghost'}
              className={`flex-1 rounded-xl ${mode === 'text' ? '' : 'hover:bg-white/5'}`}
              onClick={() => setMode('text')}
              data-testid="text-mode-btn"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Type
            </Button>
          </div>
        </div>
        
        {/* Voice Recording Interface */}
        {mode === 'voice' && (
          <div className="px-6 flex flex-col items-center">
            {/* Recording Orb */}
            <div className="relative mb-8">
              {/* Background glow */}
              <div 
                className={`absolute inset-0 rounded-full blur-3xl transition-opacity ${
                  isRecording ? 'opacity-50 orb-gradient' : 'opacity-0'
                }`}
                style={{ transform: 'scale(1.5)' }}
              />
              
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                className={`record-btn relative ${isRecording ? 'recording' : ''}`}
                data-testid="record-btn"
              >
                {isTranscribing ? (
                  <Loader2 className="w-10 h-10 text-white animate-spin" />
                ) : isRecording ? (
                  <Square className="w-10 h-10 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-white" />
                )}
              </button>
            </div>
            
            {/* Recording status */}
            <div className="text-center mb-8">
              {isRecording ? (
                <>
                  <div className="sound-bars mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="sound-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                  <span className="font-mono text-lg">{formatTime(recordingTime)}</span>
                  <p className="text-sm text-muted-foreground mt-1">Tap to stop</p>
                </>
              ) : isTranscribing ? (
                <p className="text-muted-foreground">Transcribing your dream...</p>
              ) : (
                <p className="text-muted-foreground">Tap to start recording</p>
              )}
            </div>
          </div>
        )}
        
        {/* Dream Content */}
        <div className="px-6 space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title (optional)</Label>
            <Input
              id="title"
              placeholder="Give your dream a title..."
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="bg-muted/50 border-border"
              data-testid="dream-title-input"
            />
          </div>
          
          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">
              Dream Content {mode === 'voice' && '(auto-filled from recording)'}
            </Label>
            <Textarea
              id="content"
              placeholder={mode === 'voice' 
                ? 'Your transcribed dream will appear here...' 
                : 'Describe your dream in as much detail as you can remember...'
              }
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="min-h-[200px] bg-muted/50 border-border resize-none"
              data-testid="dream-content-input"
            />
          </div>
          
          {/* Mood */}
          <div className="space-y-2">
            <Label>How did you feel upon waking?</Label>
            <Select
              value={formData.mood}
              onValueChange={(value) => setFormData({ ...formData, mood: value })}
            >
              <SelectTrigger className="bg-muted/50 border-border" data-testid="mood-select">
                <SelectValue placeholder="Select mood..." />
              </SelectTrigger>
              <SelectContent>
                {MOODS.map((mood) => (
                  <SelectItem key={mood.value} value={mood.value}>
                    {mood.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              variant="outline"
              className="flex-1 rounded-full py-6"
              onClick={() => handleSave(false)}
              disabled={isSaving || !formData.content.trim()}
              data-testid="save-dream-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
            
            <Button
              className="flex-1 rounded-full py-6 glow-sm"
              onClick={() => handleSave(true)}
              disabled={isSaving || !formData.content.trim()}
              data-testid="save-analyze-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Save & Analyze
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
